import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { GridColDef, GridRowsProp, GridTreeNodeWithRender, GridValueGetterParams } from '@mui/x-data-grid';
import { UpdateItemCustomers } from './update';
import { AddItemCustomers } from './add';
import GenericPromises from '../../../../../api/GenericPromises';
import useSnackBar from '../../../../../components/Commons/SnackBar/useSnackBar';
import { AuthContext } from '../../../../../context/AuthContext';
import { useDates } from '../../../../../hooks/useDates';
import { Menuitem } from '../../../../../interfaces/Security/menu';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { CustomerItem } from '../../../../../interfaces/Sales/Catalogs/Customers/customerItems';
import { Spinner } from '../../../../../components/Commons/Spinner/Spinner';
import { PrimaryButton } from '../../../../../theme/buttons';
import DataTable from '../../../../../components/Tables/GridTableMaterialUI/DataTable';
import { DialogEntity } from '../../../../../components/Dialogs/DialogEntity';
import { Header } from '../../../../../components/Header';
import { ErrorDisplay } from '../../../../../components/Commons/Error/ErrorDisplay';

export const TableItemCustomers = ({ ...props }) => {
    const [t] = useTranslation("global");
    const location = useLocation();
    const { GenericGetResource } = GenericPromises();
    const { showSnackBar, SnackbarComponent } = useSnackBar();
    const { user } = useContext(AuthContext);
    const [dataLoaded, setDataLoaded] = useState(false);
    const { TimeConverter } = useDates();
    const [openDialogUpdate, setOpenDialogUpdate] = useState(false);
    const [openDialogAdd, setOpenDialogAdd] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [customerItemsData, setCustomerItemsData] = useState<GridRowsProp>([]);
    const [customerItemPayload, setCustomerItemPayload] = useState({});
    const [resourceScreen, setResourceScreen] = useState<Menuitem>();
    const [messageSnack, setMessageSnack] = useState("");
    const [myPreferences, setMyPreferences] = useState({});
    const { GetResourceByUrl } = usePermissions();
    const [columnsCustomerItems, setColumnsCustomerItems] = useState<GridColDef<CustomerItem>[]>([
        { field: 'business_name', headerName: t("customeritems.fields.business_name"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'item_price', headerName: t("customeritems.fields.item_price"), headerClassName: 'header-grid-table', flex: 1, align: 'left', headerAlign: 'left', type: 'number' },
        { field: 'customer_reference', headerName: t("customeritems.fields.customer_reference"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'customer_reference_description', headerName: t("customeritems.fields.customer_reference_description"), headerClassName: 'header-grid-table', flex: 1, },
        {
            field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
        { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
    ]);



    const methodUsed = useCallback(() => {
        switch (location.state.method) {
            case "add":
                {
                    showSnackBar(t("generic.snackbar.add"), "success");
                    break;
                }
            case "delete":
                {
                    showSnackBar(t("generic.snackbar.delete"), "success");
                    break;
                }
        }

    }, [location.state, t]);

    const loadPreferences = async () => {
        let myPreferences = await localStorage.getItem("grid-customeritems-items");
        if (myPreferences != null) {
            setMyPreferences(JSON.parse(myPreferences));
        }
    }

    const loadColumnsOrder = async () => {
        let myOrderColumns = await localStorage.getItem("grid-customeritems-items-columns");
        if (myOrderColumns != null) {
            let myJson = JSON.parse(myOrderColumns);
            for (let index = 0; index < myJson.length; index++) {
                const element = myJson[index];
                if (element['type'] === 'date') {
                    element.headerName = t(`generic.${element.field}`);
                    element.valueGetter = function (params: GridValueGetterParams<CustomerItem, any, GridTreeNodeWithRender>) {
                        return TimeConverter(params.value);
                    };
                }
                if (element.field) {

                }
                else {
                    element.headerName = t(`customers.fields.${element.field}`);
                }
            }
            setColumnsCustomerItems(myJson);
        }
    }

    const handleAddRow = (newRow: CustomerItem) => {
        let myRow = {
            ...newRow,
            item_code: props.item_code,
            item_description: props.item_description,
            creation_date: new Date(),
            last_update_user: user?.user_email,
            last_update_date: new Date()
        }
        setCustomerItemsData((prevState) => [...customerItemsData, myRow]);
    };

    const handleUpdateRow = (updateRow: CustomerItem) => {
        if (customerItemsData.length === 0) {
            return;
        }
        setCustomerItemsData((prevRows) => {
            const rowToUpdateIndex = customerItemsData.findIndex(e => e.customer_items_id === updateRow.customer_items_id);

            return prevRows.map((row, index) =>
                index === rowToUpdateIndex ? updateRow : row,
            );
        });
    };

    const handleDeleteRow = (deleteRow: number) => {
        if (customerItemsData.length === 0) {
            return;
        }
        setCustomerItemsData((prevRows) => {
            const rowToDeleteIndex = customerItemsData.findIndex(e => e.customer_items_id === deleteRow);
            return [
                ...customerItemsData.slice(0, rowToDeleteIndex),
                ...customerItemsData.slice(rowToDeleteIndex + 1),
            ];
        });
    };

    useEffect(() => {
        if (location.state !== null && location.state.method) methodUsed();
        GenericGetResource(`/customeritems/byitemid/${props.item_id}`)
            .then(
                (response) => {
                    setCustomerItemsData(response.data.data);

                    GetResourceByUrl(`/customeritems`)
                        .then((response1) => {
                            setResourceScreen((prev) => response1);
                            loadColumnsOrder();
                            loadPreferences();
                            setDataLoaded(true);
                        })
                        .catch((error) => {
                            setMessageSnack(error.message);
                            showSnackBar(error.message, 'error');
                        });
                }
            ).catch((error) => {
                setHasError(true);
                setMessageSnack(error.message);
                showSnackBar(error.message, 'error');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {!dataLoaded && !hasError && <Spinner />}
            {hasError &&
                <ErrorDisplay
                    errorMessage={messageSnack}
                />
            }
            {dataLoaded && resourceScreen?.read &&
                <>
                    <div className='screen-container'>
                        {resourceScreen?.create === true &&
                            <div className='d-flex flex-row-reverse my-1'>
                                <PrimaryButton variant='outlined' className="my-1" onClick={() => { setOpenDialogAdd(true) }}>{t("generic.buttons.add")}</PrimaryButton>
                            </div>
                        }
                        <div className="d-flex justify-content-center">
                            <DataTable
                                columns={columnsCustomerItems}
                                setColumns={setColumnsCustomerItems}
                                data={customerItemsData}
                                entityId={"customer_items_id"}
                                entity={`CustomerItems`}
                                preferences={myPreferences}
                                namePreferences={"grid-customeritems-items"}
                                nameOrderColumns={"grid-customeritems-items-columns"}
                                isChildren={true}
                                setOpenDialog={setOpenDialogUpdate}
                                setDataRow={setCustomerItemPayload}
                            />
                        </div>
                    </div>
                    <DialogEntity
                        content={<UpdateItemCustomers customerItemPayload={customerItemPayload} setOpenDialog={setOpenDialogUpdate} permissions={resourceScreen} DeleteRow={handleDeleteRow} UpdateRow={handleUpdateRow} />}
                        open={openDialogUpdate}
                        setOpen={setOpenDialogUpdate}
                        title={<Header title={t("customeritems.relation")} size='sm' />}
                    />
                    <DialogEntity
                        content={<AddItemCustomers setOpenDialog={setOpenDialogAdd} item_id={props.item_id} item_code={props.item_code} item_description={props.item_description} AddNewRow={handleAddRow} />}
                        open={openDialogAdd}
                        setOpen={setOpenDialogAdd}
                        title={<Header title={t("customeritems.relation")} size='sm' />}
                    />
                    <SnackbarComponent />
                </>
            }
        </>
    )
}
